import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Breadcrumbs } from '@99designs/design-services-common';
import { FullPageSpinner, PageLayout } from '@99designs/design-services-layouts';
import {
    GenerateBriefLoadingPage,
    IntelligentBriefForm,
    MultiStepTopBar,
    useBriefContext,
    useBriefQuery,
    useGenerateMutation,
    useUpdateProductOnBriefChange,
} from '@99designs/feature-design-services-brief';
import { __, getCurrentCulture } from '@99designs/i18n';
import { usePage, useSendPageViewOnce } from '@99designs/tracking';
import { ErrorPage, NotFoundErrorPage } from '../Error';
import type { PageTracking } from '@99designs/tracking';

export default function Start({ isUbik = false }: { isUbik?: boolean }) {
    const [searchParams] = useSearchParams();
    const { pageData } = usePage();
    const [briefPageLoading, setBriefPageLoading] = useState(false);
    const locale = useMemo(() => getCurrentCulture(), []);
    const briefId = searchParams.get('id') ?? undefined;
    const tracking: PageTracking = {
        pageName: pageData.pageName,
        pageSection: pageData.pageSection,
        pageStage: pageData.pageStage,
        pageDept: 'Design Services',
    };
    const briefPath = `/experts/request/brief/${briefId}`;

    const breadcrumbs = [
        {
            url: '',
            label: __('Design Brief'),
        },
    ];

    useSendPageViewOnce(locale);

    const {
        onCompleted,
        product,
        config: {
            titleCopy,
            tenant: { name, exit, title },
        },
    } = useBriefContext();

    const {
        data,
        loading: briefLoading,
        error: briefError,
    } = useBriefQuery({
        variables: {
            id: briefId || '',
        },
        onCompleted,
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });

    const [generate, { loading: generateLoading, error: generateError }] = useGenerateMutation({
        onCompleted: () => {
            window.location.href = briefPath;
        },
    });

    useUpdateProductOnBriefChange(data?.brief);

    // generateLoading flickers from true to false when the generate mutation is complete, so we show GenerateBriefLoadingPage depending on briefPageLoading state
    useEffect(() => {
        if (generateLoading) {
            setBriefPageLoading(true);
        }
    }, [generateLoading]);

    if (!briefId) {
        return <NotFoundErrorPage />;
    }

    if (briefLoading || !data) {
        return <FullPageSpinner />;
    }

    if (briefError || generateError) {
        return <ErrorPage error={briefError || generateError} />;
    }

    const mpvId =
        data.brief.request?.expertService?.printProduct?.mpvId ||
        data.brief.product?.fulfilmentMpvId;

    if (briefPageLoading) {
        return (
            <>
                <MultiStepTopBar
                    title={title(titleCopy, data.brief.request, data.brief.product)}
                    exitUrl={exit(mpvId || '')}
                />
                <GenerateBriefLoadingPage />
            </>
        );
    }

    return isUbik ? (
        <>
            <Breadcrumbs crumbs={breadcrumbs} />
            <Helmet>
                <title>
                    {__('Design Services Brief Start')} | {name}
                </title>
            </Helmet>
            <IntelligentBriefForm
                briefId={briefId}
                product={product}
                generate={generate}
                brief={data.brief}
            />
        </>
    ) : (
        <PageLayout
            locale={locale}
            breadcrumbs={<Breadcrumbs crumbs={breadcrumbs} />}
            pageTracking={tracking}
        >
            <Helmet>
                <title>
                    {__('Design Services Brief Start')} | {name}
                </title>
            </Helmet>
            <IntelligentBriefForm
                briefId={briefId}
                product={product}
                generate={generate}
                brief={data.brief}
            />
        </PageLayout>
    );
}
